import { Link } from 'react-router-dom';
import { RoomAvailabilityState, formatMoney, type Room } from '@musicville/shared';
import { Icon } from '@/components/brand/Icon';
import { MediaFrame } from '@/components/ui/MediaFrame';
import { Badge, type Tone } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { useI18n } from '@/lib/i18n';

const TONE: Record<Room['availabilityState'], Tone> = {
  [RoomAvailabilityState.AVAILABLE]: 'success',
  [RoomAvailabilityState.LIMITED]: 'warning',
  [RoomAvailabilityState.FULL]: 'danger',
};

export function RoomCard({
  room,
  className,
}: {
  room: Room;
  className?: string | undefined;
}): React.JSX.Element {
  const { t, locale } = useI18n();
  const full = room.availabilityState === RoomAvailabilityState.FULL;

  return (
    <article
      className={cn(
        'flex flex-col overflow-hidden rounded-lg border border-border-subtle bg-surface shadow-card',
        className,
      )}
    >
      <MediaFrame ratio="landscape" src={room.photos[0]} alt={room.name} icon="door" />

      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="flex-1 text-h4 font-semibold text-primary">{room.name}</h3>
          <Badge tone={TONE[room.availabilityState]}>{room.availabilityLabel}</Badge>
        </div>

        <p className="text-body-sm text-secondary">{room.description}</p>

        <div className="flex flex-wrap items-center gap-4">
          <span className="flex items-center gap-1.5 text-caption text-secondary">
            <Icon name={(room.equipmentIcon as never) ?? 'piano'} size={16} />
            {room.equipment[0]}
          </span>
          <span className="flex items-center gap-1.5 text-caption text-secondary">
            <Icon name="users" size={16} />
            {t('rooms.upTo')} {room.capacity}{' '}
            {room.capacity === 1 ? t('rooms.person') : t('rooms.people')}
          </span>
        </div>

        <div className="mt-auto h-px bg-border-subtle" />

        <div className="flex items-center justify-between gap-4">
          <p className="flex flex-col">
            <span className="text-body font-semibold text-primary">
              {formatMoney(room.hourlyRateMinor, room.currency, locale)} {t('rooms.perHour')}
            </span>
            <span className="text-caption text-tertiary">
              {t('rooms.open')} {room.openFrom}–{room.openTo}
            </span>
          </p>
          <Link
            to={`/practice-rooms/${room.slug}`}
            className={cn(
              'inline-flex h-11 items-center justify-center rounded-md border px-6 text-body font-medium transition-colors',
              full
                ? 'border-border-default bg-surface text-tertiary hover:bg-subtle'
                : 'border-border-strong bg-surface text-primary hover:bg-subtle',
            )}
          >
            {t('rooms.check')}
          </Link>
        </div>
      </div>
    </article>
  );
}
